import React, { useEffect, useState } from 'react';
import { FaSearch, FaArrowUp, FaArrowDown, FaUsers, FaClock } from 'react-icons/fa';
import { LuCircleDollarSign, LuCircleAlert } from 'react-icons/lu';
import { FiTrendingUp } from 'react-icons/fi';
import { graphqlRequest } from '../../utils/graphqlRequest';
import './styles/DashBoard.css';

const DASHBOARD_QUERY = `
  query GetFinanceDashboard {
    getFinanceDashboard {
      totalIncome
      totalExpenses
      netProfit
      pendingPayments
      incomeTrend
      incomeTrendPositive
      expensesTrend
      expensesTrendPositive
      profitTrend
      profitTrendPositive
      pendingTrend
      pendingTrendPositive
      monthlyData {
        month
        income
        expenses
      }
      totalGrossPay
      overtimePay
      superannuation
      netPay
      totalHoursWorked
      totalEmployees
      grossPayTrend
      grossPayTrendPositive
      overtimePayTrend
      overtimePayTrendPositive
      superannuationTrend
      superannuationTrendPositive
      netPayTrend
      netPayTrendPositive
      bestMonth
      bestMonthRevenue
      avgMonthlyProfit
      monthsWithData
      outstandingBalance
      outstandingClients
    }
  }
`;

const formatCurrency = (value) => {
  if (value == null || isNaN(value)) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatNumber = (value) => {
  if (value == null || isNaN(value)) return '0';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  }).format(value);
};

const StatCard = ({ title, value, trend, icon, positive }) => (
  <div className="stat-card">
    <div className="stat-top">
      <span>{title}</span>
      <div className="icon-box">{icon}</div>
    </div>

    <h2>{value}</h2>

    <p className={positive ? 'green' : 'red'}>
      {positive ? <FaArrowUp /> : <FaArrowDown />} {trend} vs last period
    </p>
  </div>
);

const FinanceChart = ({ data }) => {
  const max = Math.max(...data.map((d) => Math.max(d.income, d.expenses)), 1);

  return (
    <div className="chart-box">
      <h3>Revenue vs Workforce Wages</h3>

      <div className="bars">
        {data.map((d) => (
          <div className="bar-group" key={d.month}>
            <div className="bar-stack">
              <div
                className="bar income"
                style={{ height: `${(d.income / max) * 220}px` }}
                title={`Revenue: ${formatCurrency(d.income)}`}
              />
              <div
                className="bar expense"
                style={{ height: `${(d.expenses / max) * 220}px` }}
                title={`Wages: ${formatCurrency(d.expenses)}`}
              />
            </div>
            <span className="month">{d.month}</span>
          </div>
        ))}
      </div>

      <div className="legend">
        <div>
          <span className="income-dot" /> Revenue (Invoices)
        </div>
        <div>
          <span className="expense-dot" /> Workforce Wages
        </div>
      </div>
    </div>
  );
};

export default function DashBoard() {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await graphqlRequest(DASHBOARD_QUERY);
      setDashboard(data.getFinanceDashboard);
    } catch (err) {
      console.error('Failed to fetch finance dashboard:', err);
      setError('Failed to load finance data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  if (loading) {
    return (
      <div className="dashboard">
        <div className="dashboard-loading">
          <div className="spinner" />
          <p>Loading finance data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard">
        <div className="dashboard-error">
          <LuCircleAlert size={40} />
          <p>{error}</p>
          <button onClick={fetchDashboard}>Retry</button>
        </div>
      </div>
    );
  }

  if (!dashboard) return null;

  const monthlyData = dashboard.monthlyData || [];

  return (
    <div className="dashboard">
      <div className="top-bar">
        <h2>Finance Overview</h2>
        <div className="search">
          <FaSearch />
          <input placeholder="Search" />
        </div>
      </div>

      {/* TOP STATS */}
      <div className="stats">
        <StatCard
          title="Total Revenue"
          value={formatCurrency(dashboard.totalIncome)}
          trend={dashboard.incomeTrend}
          positive={dashboard.incomeTrendPositive}
          icon={<LuCircleDollarSign />}
        />
        <StatCard
          title="Workforce Wages"
          value={formatCurrency(dashboard.totalExpenses)}
          trend={dashboard.expensesTrend}
          positive={dashboard.expensesTrendPositive}
          icon={<FaUsers />}
        />
        <StatCard
          title="Net Profit"
          value={formatCurrency(dashboard.netProfit)}
          trend={dashboard.profitTrend}
          positive={dashboard.profitTrendPositive}
          icon={<FiTrendingUp />}
        />
        <StatCard
          title="Pending Payments"
          value={formatCurrency(dashboard.pendingPayments)}
          trend={dashboard.pendingTrend}
          positive={dashboard.pendingTrendPositive}
          icon={<LuCircleAlert />}
        />
      </div>

      {/* MAIN CHART */}
      <FinanceChart data={monthlyData} />

      {/* WORKFORCE WAGES BREAKDOWN */}
      <div className="section-header">
        <h3 className="section-title">Workforce Wages Breakdown</h3>
        <div className="workforce-meta">
          <span className="meta-badge">
            <FaUsers /> {dashboard.totalEmployees}{' '}
            {dashboard.totalEmployees === 1 ? 'Employee' : 'Employees'}
          </span>
          <span className="meta-badge">
            <FaClock /> {formatNumber(dashboard.totalHoursWorked)} Hours Worked
          </span>
        </div>
      </div>

      <div className="stats">
        <StatCard
          title="Gross Pay"
          value={formatCurrency(dashboard.totalGrossPay)}
          trend={dashboard.grossPayTrend}
          positive={dashboard.grossPayTrendPositive}
          icon={<LuCircleDollarSign />}
        />
        <StatCard
          title="Overtime Pay"
          value={formatCurrency(dashboard.overtimePay)}
          trend={dashboard.overtimePayTrend}
          positive={dashboard.overtimePayTrendPositive}
          icon={<FaClock />}
        />
        <StatCard
          title="Superannuation"
          value={formatCurrency(dashboard.superannuation)}
          trend={dashboard.superannuationTrend}
          positive={dashboard.superannuationTrendPositive}
          icon={<FiTrendingUp />}
        />
        <StatCard
          title="Net Pay (Take-home)"
          value={formatCurrency(dashboard.netPay)}
          trend={dashboard.netPayTrend}
          positive={dashboard.netPayTrendPositive}
          icon={<LuCircleDollarSign />}
        />
      </div>

      {/* BOTTOM SUMMARY CARDS */}
      <div className="summary-grid">
        <div className="summary-card blue">
          <div>
            <p className="label">Best Performing Month</p>
            <h3>{dashboard.bestMonth}</h3>
            <span>{formatCurrency(dashboard.bestMonthRevenue)} in revenue</span>
          </div>
        </div>

        <div className="summary-card green">
          <div>
            <p className="label">Average Monthly Profit</p>
            <h3>{formatCurrency(dashboard.avgMonthlyProfit)}</h3>
            <span>
              Across {dashboard.monthsWithData}{' '}
              {dashboard.monthsWithData === 1 ? 'month' : 'months'}
            </span>
          </div>
        </div>

        <div className="summary-card orange">
          <div>
            <p className="label">Outstanding Balance</p>
            <h3>{formatCurrency(dashboard.outstandingBalance)}</h3>
            <span>
              From {dashboard.outstandingClients}{' '}
              {dashboard.outstandingClients === 1 ? 'client' : 'clients'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
